import axios from "axios";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  clearAdminSessionUser,
  clearCustomerSessionUser,
  getAdminSessionUser,
  getCustomerSessionUser,
  isAdminUser,
  loginCustomerUser,
  loginAdminUser,
  registerStoredUser,
  setAdminSessionUser,
  setCustomerSessionUser,
  updateStoredUser,
} from "./utils/auth";
import { apiUrl } from "./utils/config";
import { formatPrice as _formatPrice } from "./utils/currency";

/* -------------------------------------------------------------------------- */
/*                                   CONFIG                                   */
/* -------------------------------------------------------------------------- */

const AppContext = createContext();

const API_BASE = apiUrl;
const LICENSE_ID = 3;
const CASHIER_ID = 2;

const STORAGE_BILL_ID = "cart_bill_id";

/* -------------------------------------------------------------------------- */
/*                               STORAGE HELPERS                              */
/* -------------------------------------------------------------------------- */

const saveBillId = (id) => localStorage.setItem(STORAGE_BILL_ID, id);
const getBillId = () => localStorage.getItem(STORAGE_BILL_ID);
const clearBillId = () => localStorage.removeItem(STORAGE_BILL_ID);

/* -------------------------------------------------------------------------- */
/*                                 PROVIDER                                   */
/* -------------------------------------------------------------------------- */

export const AppProvider = ({ children }) => {
  /* --------------------------------- STATE -------------------------------- */

  const [cart, setCart] = useState([]);
  const [billId, setBillId] = useState(null);
  const [customerUser, setCustomerUser] = useState(() => getCustomerSessionUser());
  const [adminUser, setAdminUser] = useState(() => getAdminSessionUser());

  const [wishlist, setWishlist] = useState([]);
  const [orders, setOrders] = useState([]);

  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const [blogPosts, setBlogPosts] = useState([]);

  const [storeSettings, setStoreSettings] = useState({
    store_name: "Gamapa Foods",
    store_currency: "USD",
    store_email: "",
    store_phone: "",
    store_address: "",
    store_logo: null,
    tax_rate: 5.0,
    low_stock_threshold: 10,
    notifications: {},
  });

  const syncTimer = useRef(null);

  /* ------------------------------------------------------------------------ */
  /*                             BILL UTILITIES                                */
  /* ------------------------------------------------------------------------ */

  const buildBillPayload = (cartItems) => ({
    status: "pending",
    total_discount: 0,
    license_id: LICENSE_ID,
    cashier_id: CASHIER_ID,
    items: cartItems && cartItems.length > 0 ? cartItems.map((item) => ({
      menu_item_id: item.id,
      quantity: item.quantity,
      price: item.price,
      discount: 0,
      item: null,
    })) : [],
  });

  const syncBill = async (updatedCart) => {
    //if (!updatedCart.length) return;

    const payload = buildBillPayload(updatedCart);

    try {
      if (!billId) {
        const res = await axios.post(`${API_BASE}/bill`, payload);
        const newBillId = res.data.message.bill_id;

        setBillId(newBillId);
        saveBillId(newBillId);
      } else {
        payload.id = billId;
        await axios.put(`${API_BASE}/bill`, payload);
      }
    } catch (err) {
      console.error("Bill sync failed:", err);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                             DEBOUNCED SYNC                                */
  /* ------------------------------------------------------------------------ */

  const debouncedSync = (updatedCart) => {
    if (syncTimer.current) clearTimeout(syncTimer.current);

    syncTimer.current = setTimeout(() => {
      syncBill(updatedCart);
    }, 400);
  };

  /* ------------------------------------------------------------------------ */
  /*                               CART LOGIC                                  */
  /* ------------------------------------------------------------------------ */

  const addToCart = (product) => {
    setIsCartOpen(true);

    setCart((prev) => {
      const updatedCart = prev.find((i) => i.id === product.id)
        ? prev.map((i) =>
            i.id === product.id
              ? { ...i, quantity: i.quantity + 1 }
              : i
          )
        : [...prev, { ...product, quantity: 1 }];

      debouncedSync(updatedCart);
      return updatedCart;
    });
  };

  const updateQty = (id, delta) => {
    console.log(delta,"delata")
    setCart((prev) => {
      const updatedCart = prev.map((item) =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item
      );

      debouncedSync(updatedCart);
      return updatedCart;
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) => {
      const updatedCart = prev.filter((item) => item.id != id);
      debouncedSync(updatedCart);
      return updatedCart;
    });
  };

  const isInCart = (id) => {
    return cart.some((item) => item.id === id);
  };

  const clearCart = () => {
    setCart([]);
    setBillId(null);
    clearBillId();
  };

  /* ------------------------------------------------------------------------ */
  /*                             DERIVED VALUES                                */
  /* ------------------------------------------------------------------------ */

  const cartTotal = cart.reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity),
    0
  );

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartItemsCount = cart.length;
  /* ------------------------------------------------------------------------ */
  /*                             WISHLIST LOGIC                                */
  /* ------------------------------------------------------------------------ */

  const toggleWishlist = (product) => {
    setWishlist((prev) =>
      prev.some((i) => i.id === product.id)
        ? prev.filter((i) => i.id !== product.id)
        : [...prev, product]
    );
  };

  /* ------------------------------------------------------------------------ */
  /*                               ORDER LOGIC                                 */
  /* ------------------------------------------------------------------------ */

  const addOrder = (orderDetails) => {
    const order = {
      id: Date.now(),
      date: new Date().toISOString(),
      items: cart,
      total: cartTotal,
      ...orderDetails,
    };

    setOrders((prev) => [order, ...prev]);
    clearCart();
    setIsPaymentOpen(false);
    setIsCartOpen(false);
  };

  /* ------------------------------------------------------------------------ */
  /*                               AUTH LOGIC                                  */
  /* ------------------------------------------------------------------------ */

  const persistCustomerSession = (result) => {
    if (!result.success) {
      return result;
    }

    setCustomerSessionUser(result.user);
    setCustomerUser(result.user);

    return result;
  };

  const loginCustomer = async (credentials) => {
    const result = await loginCustomerUser(credentials);

    return persistCustomerSession(result);
  };

  const loginAdmin = async (credentials) => {
    const result = await loginAdminUser(credentials);

    if (!result.success) {
      return result;
    }

    setAdminSessionUser(result.user);
    setAdminUser(result.user);

    return result;
  };

  const register = async (payload) => {
    const result = await registerStoredUser(payload);

    return persistCustomerSession(result);
  };

  const logoutCustomer = () => {
    clearCustomerSessionUser();
    setCustomerUser(null);
    setWishlist([]);
    setOrders([]);
    clearCart();
  };

  const logoutAdmin = () => {
    clearAdminSessionUser();
    setAdminUser(null);
  };

  const updateUserProfile = async (updates) => {
    if (!customerUser) return null;

    const nextUser = await updateStoredUser({ ...customerUser, ...updates });
    setCustomerSessionUser(nextUser);
    setCustomerUser(nextUser);

    return nextUser;
  };

  /* ------------------------------------------------------------------------ */
  /*                            STORE SETTINGS                                 */
  /* ------------------------------------------------------------------------ */

  const loadStoreSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/settings`);
      if (res.data?.status) setStoreSettings(res.data.settings);
    } catch (e) {
      console.error("Failed to load store settings", e);
    }
  }, []);

  // Returns a price string formatted with the store's currency
  const formatPrice = useCallback(
    (amount) => _formatPrice(amount, storeSettings.store_currency || "USD"),
    [storeSettings.store_currency]
  );

  /* ------------------------------------------------------------------------ */
  /*                              BLOG POSTS                                   */
  /* ------------------------------------------------------------------------ */

  const loadBlogPosts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/blog`);
      setBlogPosts(res.data?.list || []);
    } catch (e) {
      console.error("Failed to load blog posts", e);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                         CART REHYDRATION (ONCE)                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const hydrateCart = async () => {
      const storedBillId = getBillId();
      if (!storedBillId) return;

      try {
        const res = await axios.get(`${API_BASE}/bill/${storedBillId}`);
        const bill = res.data;

        if (!bill || !bill.item || !bill.item.items) {
          console.log(bill)
          return;
        } 

        const hydratedCart = bill.item.items.map((item) => ({
          id: item.menu_item_id,
          quantity: item.quantity,
          price: item.price,
          images:item.item.images
        }));

        setCart(hydratedCart);
        setBillId(storedBillId);
      } catch (err) {
        console.error("Cart hydration failed:", err);
        
      }
    };

    hydrateCart();
    loadBlogPosts();
    loadStoreSettings();
    setCustomerUser(getCustomerSessionUser());
    setAdminUser(getAdminSessionUser());

    return () => syncTimer.current && clearTimeout(syncTimer.current);
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                               CONTEXT VALUE                               */
  /* ------------------------------------------------------------------------ */

  return (
    <AppContext.Provider
      value={{
        cart,
        cartTotal,
        cartCount,
        cartItemsCount,
        addToCart,
        updateQty,
        removeFromCart,
        clearCart,
        isInCart, 
        wishlist,
        toggleWishlist,

        orders,
        addOrder,

        customerUser,
        adminUser,
        user: customerUser,
        isAuthenticated: Boolean(customerUser),
        isAdminAuthenticated: Boolean(adminUser),
        isAdmin: isAdminUser(adminUser),
        loginCustomer,
        loginAdmin,
        register,
        logoutCustomer,
        logoutAdmin,
        logout: logoutCustomer,
        updateUserProfile,

        isCartOpen,
        setIsCartOpen,
        isPaymentOpen,
        setIsPaymentOpen,

        blogPosts,
        loadBlogPosts,

        billId,

        storeSettings,
        setStoreSettings,
        loadStoreSettings,
        formatPrice,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   HOOK                                     */
/* -------------------------------------------------------------------------- */

export const useAppContext = () => useContext(AppContext);
